/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { type ComponentType, memo } from 'react';

import { componentWithCondition } from '../component-with-condition/index.tsx';

/**
 * A utility function that conditionally applies React.memo on a component based on a provided condition.
 * Useful for if you want to memoize a component that uses a relay fragment, so that you don't need
 * to rename the component and cause relay/linting to change the fragment names.
 */
export const memoWithCondition: <P extends {}>(
	condition: () => boolean,
	Component: ComponentType<P>,
	propsAreEqual?: (prevProps: Readonly<P>, nextProps: Readonly<P>) => boolean,
) => import('react').FC<
	(
		| import('react').PropsWithoutRef<
				import('react').PropsWithoutRef<P> &
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
					import('react').RefAttributes<import('react').Component<P, any, any>>
		  >
		| import('react').PropsWithoutRef<import('react').PropsWithRef<P>>
	) &
		import('react').PropsWithoutRef<P> &
		import('../component-with-condition/index.tsx').GetRefAttributes<
			import('react').PropsWithRef<
				| (import('react').PropsWithoutRef<P> &
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any
						import('react').RefAttributes<import('react').Component<P, any, any>>)
				| import('react').PropsWithRef<P>
			>,
			import('react').PropsWithRef<P>
		>
> = <P extends {}>(
	condition: () => boolean,
	Component: ComponentType<P>,
	propsAreEqual?: (prevProps: Readonly<P>, nextProps: Readonly<P>) => boolean,
) => {
	return componentWithCondition(condition, memo(Component, propsAreEqual), Component);
};
